import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TenantService } from '../../services/tenant.service';
import { OnboardingService } from '../../services/onboarding.service';
import { ProjectContextService } from '../../services/project-context.service';
import { ThemeService } from '../../services/theme.service';
import { ToastService } from '../../services/toast.service';
import { Project } from '../../models/project.model';
import { ProjectNameDialogComponent } from '../shared/project-name-dialog.component';
import { ManageProjectsDialogComponent } from '../shared/manage-projects-dialog.component';
import { AvatarComponent } from '../shared/ui/ui';

@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    AvatarComponent
  ],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss'
})
export class TopBarComponent implements OnInit {

  private authService = inject(AuthService);
  private tenantService = inject(TenantService);
  private onboardingService = inject(OnboardingService);
  private dialog = inject(MatDialog);
  private toast = inject(ToastService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);
  readonly projectContext = inject(ProjectContextService);

  /* ---- Precomputed template state ------------------------------------------
     The shell renders on every change-detection pass of every screen, so it
     carries no getters or method calls in its template at all. See the
     angular-performance skill. */

  activeProjectName = 'Select project';
  switcherAriaLabel = 'Switch project';
  projectCountLabel = '';
  canCreateProject = false;

  isDarkMode = false;
  themeIcon = 'light_mode';
  themeTooltip = 'Switch to dark theme';

  userName = '';
  userEmail = '';
  accountAriaLabel = 'Account menu';

  /** Active-project cap from the plan. null = unlimited; an absent entitlement defaults to 1. */
  private maxProjects: number | null = 1;

  ngOnInit() {
    const user = this.authService.getUserDetails();
    this.userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || 'Your account';
    this.userEmail = user?.email ?? '';
    this.accountAriaLabel = `Account menu for ${this.userName}`;

    // Tracks OS changes too, while the preference is still 'system'.
    this.themeService.resolved$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(theme => {
        this.isDarkMode = theme === 'dark';
        this.themeIcon = this.isDarkMode ? 'dark_mode' : 'light_mode';
        this.themeTooltip = this.isDarkMode ? 'Switch to light theme' : 'Switch to dark theme';
      });

    this.projectContext.activeProject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(project => {
        this.activeProjectName = project?.name ?? 'Select project';
        this.switcherAriaLabel = project
          ? `Current project: ${project.name}. Switch project`
          : 'Select a project';
        this.refreshProjectCount();
      });

    this.projectContext.projects$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshProjectCount());

    this.projectContext.loadProjects().subscribe();

    this.tenantService.getTenantPlan().subscribe(view => {
      const max = view?.plan?.entitlements?.projects?.max;
      this.maxProjects = max === undefined ? 1 : max;
      this.refreshProjectCount();
    });
  }

  trackByProjectId(_index: number, project: Project): string {
    return project.id;
  }

  selectProject(id: string): void {
    if (id === this.projectContext.activeProjectId) return;
    this.projectContext.setActiveProjectId(id);
    this.afterProjectChange();
  }

  openNewProject(): void {
    if (!this.canCreateProject) return;
    this.dialog.open(ProjectNameDialogComponent, {
      width: '440px',
      data: {
        title: 'New project',
        confirmText: 'Create project',
        icon: 'add_circle',
        subtitle: 'A project is a separate agent, with its own knowledge, settings and widget.'
      }
    }).afterClosed().subscribe((name: string | undefined) => {
      if (!name) return;
      this.projectContext.createProject(name).subscribe({
        next: () => this.afterProjectChange(),
        error: (err) => {
          this.toast.error(err?.status === 402
            ? 'You have reached your plan’s project limit. Upgrade to add more agents.'
            : 'Could not create the project. Please try again.');
        }
      });
    });
  }

  openManage(): void {
    const before = this.projectContext.activeProjectId;
    this.dialog.open(ManageProjectsDialogComponent, { width: '480px' })
      .afterClosed().subscribe(() => {
        // Only reload the page if the active project changed (e.g. it was deactivated).
        if (this.projectContext.activeProjectId !== before) this.afterProjectChange();
      });
  }

  toggleTheme() {
    this.themeService.toggle();
  }

  goToProfile() {
    this.router.navigate(['/profile']);
  }

  logout() {
    this.authService.logout().then(() => {
      this.router.navigate(['/login']);
    });
  }

  /** "2 of 3", or just the count when the plan is unlimited. */
  private refreshProjectCount(): void {
    const used = this.projectContext.activeProjects.length;
    this.canCreateProject = this.maxProjects === null || used < this.maxProjects;
    this.projectCountLabel = this.maxProjects === null ? `${used}` : `${used} of ${this.maxProjects}`;
  }

  /** After switching/creating: refresh app-shell state and reload the current page's data. */
  private afterProjectChange(): void {
    this.onboardingService.refresh();
    const url = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(url));
  }
}
